import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { IMAGES, TRIP, whatsappUrl } from "../../data";

const titleLines = ["Islandia", "Salvaje"];
const DELAY = 2.0; // wait for preloader

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero" className="relative h-[100svh] w-full overflow-hidden bg-ink">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={IMAGES.hero} alt="Aurora boreal sobre paisaje volcánico de Islandia" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/30" />
      </motion.div>

      <motion.div style={{ y: textY, opacity }} className="relative z-10 h-full mx-auto max-w-[1400px] px-5 sm:px-8 flex flex-col justify-end pt-28 pb-16 sm:pb-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: DELAY, duration: 0.6 }}
          className="overline mb-6 flex items-center gap-3 max-w-[80%]"
        >
          <span className="h-px w-8 bg-glacier" style={{ backgroundColor: "var(--glacier)" }} />
          Aventura a medida · {TRIP.departure}
        </motion.div>

        <h1 className="font-display font-light text-[var(--paper)] leading-[0.85] tracking-tight text-[19vw] sm:text-[15vw] lg:text-[12rem]">
          {titleLines.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: DELAY + i * 0.14, duration: 1, ease: [0.65, 0, 0.35, 1] }}
              >
                {i === 1 ? <em className="italic text-ember font-normal">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: DELAY + 0.5, duration: 0.9 }}
            className="font-body font-light text-base sm:text-lg text-[var(--paper)]/80 max-w-md leading-relaxed"
          >
            Una expedición íntima, 100% hecha a medida, fuera de los circuitos.
            Cuevas de hielo, glaciares y la caza de auroras — con acompañamiento
            integral desde España.
          </motion.p>

          <motion.a
            href={whatsappUrl()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: DELAY + 0.65, duration: 0.9 }}
            className="btn-glacier group inline-flex items-center justify-between gap-6 border border-glacier px-8 py-5 text-glacier hover:text-ink transition-colors duration-300 min-w-[240px]"
            style={{ borderColor: "var(--glacier)" }}
          >
            <span className="font-body font-semibold uppercase tracking-[0.2em] text-xs">Reserva tu plaza</span>
            <ArrowDown className="h-4 w-4 -rotate-90 transition-transform duration-300 group-hover:translate-x-1" />
          </motion.a>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: DELAY + 1, duration: 1 }}
        className="absolute bottom-6 right-5 sm:right-8 z-10 flex items-center gap-3 text-[var(--paper)]/50"
      >
        <span className="font-body text-[0.65rem] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown className="h-4 w-4" />
        </motion.div>
      </motion.div>
    </section>
  );
};
